import {AsyncPipe, Location, NgClass, NgStyle} from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  InputSignal,
  OnDestroy,
  Signal,
  ViewChild,
  viewChild
} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Params, Router} from '@angular/router';
import {FormsModule, NgModel} from '@angular/forms';
import {DsButtonModule, DsFormFieldModule, DsToggleButtonModule} from '@bmw-ds/components';
import {environment} from '@environments/environment';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {IServerApi} from '@service-and-repairs/awpintegrationlib';
import {Subscription} from 'rxjs';
import {filter} from 'rxjs/operators';
import {AnalyticsAction} from '../../../core/analytics/enums/analytics-action';
import {VehicleSearchOrigin} from '../../../core/analytics/enums/vehicle-search-origin';
import {AnalyticsService} from '../../../core/analytics/services/analytics.service';
import {CustomerLoader} from '../../../core/customer/services/customer.loader';
import {DealerDataService} from '../../../core/dealer/services/dealer-data.service';
import {ServiceCase} from '../../../core/service-case/models/service-case';
import {ServiceCaseHolder} from '../../../core/service-case/models/service-case-holder';
import {ServiceCaseManager} from '../../../core/service-case/models/service-case-manager';
import {UserService} from '../../../core/user/services/user.service';
import {AwpClientLibService} from '../../../services/awp-client-lib.service';
import {
  CustomerSelectionOverlayComponent
} from '../../dms-search/customer-selection/customer-selection-overlay.component';
import {VehicleSearchService} from '../../vehicle/vehicle-search-overlay/vehicle-search.service';
import {
  VehicleSelectionOverlayComponent
} from '../../vehicle/vehicle-selection-overlay/vehicle-selection-overlay.component';
import {CustomerLanguagePopupComponent} from '../customer/customer-language-popup/customer-language-popup.component';
import {CaseSelectionOverlayComponent} from './selection/case-selection-overlay.component';
import {ServiceCaseTabComponent} from './service-case-tab/service-case-tab.component';
import {ShoppingCartButtonComponent} from './shopping-cart-button/shopping-cart-button.component';
import {VehicleSearchSuggestionsComponent} from './vehicle-search-suggestions/vehicle-search-suggestions.component';

@Component({
  selector: 'app-service-case-bar',
  templateUrl: './service-case-bar.component.html',
  styleUrls: ['./service-case-bar.component.scss'],
  imports: [
    NgClass,
    DsFormFieldModule,
    DsButtonModule,
    DsToggleButtonModule,
    FormsModule,
    ServiceCaseTabComponent,
    NgStyle,
    ShoppingCartButtonComponent,
    VehicleSelectionOverlayComponent,
    CaseSelectionOverlayComponent,
    CustomerLanguagePopupComponent,
    CustomerSelectionOverlayComponent,
    AsyncPipe,
    TranslateModule,
    VehicleSearchSuggestionsComponent
  ]
})
export class ServiceCaseBarComponent implements AfterViewInit, OnDestroy {
  private readonly analyticsService: AnalyticsService = inject(AnalyticsService);
  serviceCaseHolder: ServiceCaseHolder = inject(ServiceCaseHolder);
  private readonly serviceCaseManager: ServiceCaseManager = inject(ServiceCaseManager);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly location: Location = inject(Location);
  private readonly router: Router = inject(Router);
  userService: UserService = inject(UserService);
  private readonly dealerDataService: DealerDataService = inject(DealerDataService);
  private readonly customerLoader: CustomerLoader = inject(CustomerLoader);
  private readonly awpClientLibService: AwpClientLibService = inject(AwpClientLibService);
  readonly vehicleSearchService: VehicleSearchService = inject(VehicleSearchService);
  private readonly changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);

  readonly shortListViewState: InputSignal<{ visible: boolean }> = input<{ visible: boolean }>({visible: false});
  readonly advancedSearchVisible: Signal<boolean> = computed(() => this.vehicleSearchService.advancedSearchVisible());

  readonly searchInputValidationRegexp: string = VehicleSearchService.SEARCH_INPUT_VALIDATION_REGEXP;

  /**
   * The key pool is only available in the US market, so the button that opens it is hidden everywhere else.
   */
  readonly keyPoolButtonVisible: boolean = environment.hub === 'US';

  searchInput: string = '';
  awpLib: IServerApi;
  tabWidth: number = 0;
  loading: boolean = false;

  private hasSubscribedToDeepLinkQueryParams: boolean = false;

  private readonly vehicleSelectionOverlay: Signal<VehicleSelectionOverlayComponent> =
    viewChild<VehicleSelectionOverlayComponent>('vehicleSelectionOverlay');
  private readonly caseSelectionOverlay: Signal<CaseSelectionOverlayComponent> =
    viewChild<CaseSelectionOverlayComponent>('caseSelectionOverlay');
  private readonly searchSuggestions: Signal<VehicleSearchSuggestionsComponent> =
    viewChild<VehicleSearchSuggestionsComponent>('searchSuggestions');

  @ViewChild('tabContainer', {static: false})
  private readonly tabContainer: ElementRef;

  private queryParamsSubscription: Subscription;
  private firstQueryParamChange: boolean = true;
  // Becomes true once a service case has been active at least once. Until then, the deep link URL must not be cleared,
  // so a reload during the initial load/restore window keeps the 'service-case-id' parameter and can restore open cases.
  private hasEverHadActiveCase: boolean = false;

  constructor() {
    this.awpLib = this.awpClientLibService.serverApi;

    this.userService.user$.subscribe((): void => {
      if (!this.hasSubscribedToDeepLinkQueryParams) {
        this.hasSubscribedToDeepLinkQueryParams = true;
        this.checkParams();
      }
    });

    this.serviceCaseHolder.activeServiceCaseChanged.subscribe(() => this.setDeepLinkUrl());
    // make sure route parameters are not lost if user navigates
    this.router.events
      .pipe(filter((event): boolean => event instanceof NavigationEnd))
      .subscribe(this.setDeepLinkUrl.bind(this));

    this.serviceCaseManager.caseLoadingStarted.subscribe((): void => {
      this.loading = true;
    });
    this.serviceCaseManager.caseLoadingFinished.subscribe(
      (): void => {
        this.updateTabWidth();
        this.loading = false;
      }
    );
  }

  titleResource: string;
  src?: string;
  showLoadingIndicator?: boolean;
  showErrorIndicator?: boolean;

  ngAfterViewInit(): void {
    this.serviceCaseManager.setSelectionOverlays(
      this.vehicleSelectionOverlay(),
      this.caseSelectionOverlay()
    );
    this.setUpResizeObserver();
  }

  ngOnDestroy(): void {
    this.queryParamsSubscription?.unsubscribe();
  }

  toggleAdvancedSearchOverlay(): void {
    this.vehicleSearchService.toggleAdvancedSearch();
  }

  showSearchOverlayKeyPool(): void {
    this.vehicleSearchService.openKeyPoolSearch();
  }

  /**
   * Identifies a vehicle or service case from the content of the search input field and opens it. If the input is
   * invalid, the input field is marked as touched so that the validation error is shown.
   * @param searchInputModel NG model of the search input field.
   */
  searchAndOpenServiceCase(searchInputModel?: NgModel): void {
    this.searchSuggestions()?.hide();
    if (this.vehicleSearchService.searchAndOpenServiceCase(this.searchInput)) {
      this.searchInput = '';
    } else {
      searchInputModel?.control.markAsTouched();
    }
  }

  /**
   * Takes the search string the user has picked from the suggestion list and searches for it.
   * @param suggestion Search string that has been picked.
   * @param searchInputModel NG model of the search input field.
   */
  selectSearchSuggestion(suggestion: string, searchInputModel?: NgModel): void {
    this.searchInput = suggestion;
    this.searchAndOpenServiceCase(searchInputModel);
  }

  updateTabWidth(): void {
    const tabContainer = this.tabContainer?.nativeElement;
    if (this.serviceCaseHolder?.serviceCases.length > 0 && tabContainer?.offsetWidth) {
      // container should have 16px space on the right
      const width = (tabContainer.offsetWidth - 16) / this.serviceCaseHolder.serviceCases.length;
      // each tab has a padding-right of 8px
      this.tabWidth = width - 8;
    } else {
      this.tabWidth = 290;
    }
  }

  onCaseTabClick(serviceCase: ServiceCase): void {
    this.vehicleSearchService.closeAdvancedSearch();

    const previousServiceCaseId: string = this.serviceCaseHolder.getActiveCase()?.getExternalId();
    this.serviceCaseHolder.openServiceCaseOrSetActive(serviceCase, true);
    this.analyticsService.postServiceCaseTabEvent(
      AnalyticsAction.SWITCH_TO_OPEN_TAB,
      previousServiceCaseId,
      serviceCase
    );
  }

  onTabMouseOver(event: MouseEvent): void {
    // tooltip is only visible if tab does not have full width
    if (this.tabWidth < 290) {
      // make sure case tab tooltip stays inside screen
      const tabTooltip: HTMLElement = (event.target as HTMLElement).nextSibling as HTMLElement;
      tabTooltip?.style?.removeProperty('right');
      if (tabTooltip?.getBoundingClientRect().right > window.innerWidth) {
        tabTooltip.style.right = '0';
      }
    }
  }

  showShortList(): void {
    this.shortListViewState().visible = true;
  }

  /**
   * Sets the query parameters of the url according to the currently opened service case.
   */
  private setDeepLinkUrl(): void {
    const activeCaseId: string = this.serviceCaseHolder.getActiveCase()?.getExternalId();
    if (activeCaseId) {
      this.hasEverHadActiveCase = true;
    } else if (!this.hasEverHadActiveCase) {
      // No case has been active yet (initial load/restore in progress). Do not strip a deep-linked 'service-case-id'
      // from the URL: otherwise a reload during this window (e.g. after saving the outlet configuration) would boot
      // without the parameter and skip restoring the previously open service cases.
      return;
    }
    const queryParam: string = activeCaseId ? 'service-case-id=' + activeCaseId : undefined;
    const location: string = this.location.path().split('?')[0];
    this.location.go(location, queryParam);
  }

  /**
   * If the 'user-id' query parameter is set, it is checked if it matches the current user. If not, user is logged out.
   * If the query parameter 'service-case-id' is set, the service case with this external id will be loaded and shown.
   * If the query parameters 'vin' or 'typecode' are set, the (virtual) vehicle will be loaded and added to a new
   * service case. The new service case will be shown.
   * If the query parameter 'business-number' is set, the related business will be chosen as the selected outlet.
   */
  private checkParams(): void {
    // update https://atc.bmwgroup.net/confluence/spaces/ISPI/pages/2364375127/AWP+entry+points if parameters change
    this.queryParamsSubscription?.unsubscribe();
    // Read the initial URL directly (independent of the router's async queryParams emissions) to know whether the app
    // was loaded/reloaded WITH a deep-linked service case. Only then may previously open cases be restored from local
    // storage. Without this, a param-less start followed by opening a case from within the app (which navigates with a
    // service-case-id) would wrongly resurrect stale cases from a previous session.
    const loadedWithDeepLinkedCase: boolean =
      new URLSearchParams(this.location.path().split('?')[1] ?? '').has('service-case-id');
    this.queryParamsSubscription = this.route.queryParams.subscribe((params: Params): void => {
      // Depending on the environment, Angular may emit an initial empty {} from route.queryParams before the actual
      // URL query params on page load/reload (observed in Edge, but not in the embedded Chromium of the thin client).
      // Ignore empty emissions instead of blindly skipping the first one, so the first *real* params are always
      // treated as the initial load (restoring open cases) regardless of environment.
      if (!params || Object.keys(params).length === 0) {
        return;
      }
      if (this.requiresReLogin(params)) {
        this.logOutAndRedirect(params);
        return;
      }
      if (params?.['business-number']) {
        this.dealerDataService.switchToOutlet(params['business-number']);
      }
      if (params?.['service-case-id']) {
        this.handleServiceCaseIdQueryParameter(
          params['service-case-id'],
          params.vin,
          this.firstQueryParamChange && loadedWithDeepLinkedCase
        );
      } else if (params?.vin) {
        this.handleVinQueryParameter(params.vin);
      } else if (params?.typecode) {
        this.handleTypeCodeQueryParameter(params.typecode);
      }
      this.firstQueryParamChange = false;
    });
  }

  private handleServiceCaseIdQueryParameter(serviceCaseId: string, vin: string, restoreServiceCases: boolean): void {
    const serviceCaseIdsToRestore: string[] =
      restoreServiceCases ?
        this.serviceCaseHolder.getServiceCaseIdsFromLocalStorage() :
        [];

    const idsToRestore: string[] = serviceCaseIdsToRestore.filter((id: string): boolean => id !== serviceCaseId);

    this.serviceCaseManager.openExistingServiceCaseById(
      serviceCaseId,
      VehicleSearchOrigin.CASE_ID,
      idsToRestore.length > 0 // skip updating open service cases in local storage
    )
      .catch((): void => {
        // service case could not be loaded, try VIN instead
        if (vin) {
          this.handleVinQueryParameter(vin);
        }
      })
      .finally((): void => this.serviceCaseManager.restoreNonActiveServiceCases(idsToRestore));
  }

  private handleVinQueryParameter(vin: string): void {
    if (vin.length !== 17) {
      this.awpLib.notifyWarning(this.translate.instant('common.deepLinkVinInvalid'));
    } else {
      this.serviceCaseManager.selectAndOpenServiceCaseByVin(vin, VehicleSearchOrigin.VIN17);
    }
  }

  private handleTypeCodeQueryParameter(typeCode: string): void {
    if (typeCode.length !== 4) {
      this.awpLib.notifyWarning(this.translate.instant('common.deepLinkTypeInvalid'));
    } else {
      this.serviceCaseManager.openNewServiceCaseByTypeCode(typeCode, VehicleSearchOrigin.TYPE_CODE);
    }
  }

  /**
   * Returns true if the user needs to be re-logged-in: if a 'user-id' query param is provided that does not match the
   * current user's ID (case-insensitive).
   *
   * The check is inherently idempotent: after the re-login the current user matches the provided user-id, so the
   * restored deep link does not trigger another logout - no extra one-shot flag or forced navigation is required.
   * The 'user-id' is a separate query param and is not forwarded to integrated apps such as AIR.
   */
  private requiresReLogin(params: Params): boolean {
    const userId: string = params?.['user-id'];
    if (userId) {
      const currentUser = this.userService.getCurrentUser();
      return currentUser && currentUser.getId()?.toLowerCase() !== userId.toLowerCase();
    }
    // INTERIM ISPI-481001 — START: until ISTA sends a 'user-id', use the FORK_CONTEXT_ID as the jump marker.
    return this.interimRequiresReLogin(params);
    // INTERIM ISPI-481001 — END
  }

  // INTERIM ISPI-481001 — delete together with the call in requiresReLogin (see SRPLM-5611).
  // Treats an ISTA jump (FORK_CONTEXT_ID present in 'params') as a login change so the next mechanic must
  // re-login - even if the FORK_CONTEXT_ID is identical to the previous jump. The single post-login restore
  // is marked with 'awp-fork-restored' and suppressed exactly once.
  private interimRequiresReLogin(params: Params): boolean {
    if (!this.extractForkContextId(params)) {
      return false;
    }
    if (params?.['awp-fork-restored']) {
      this.stripForkRestoredMarker(params);
      return false;
    }
    return true;
  }

  // INTERIM ISPI-481001 — delete together with the block above.
  private extractForkContextId(params: Params): string | undefined {
    const paramsJson: string = params?.['params'];
    if (!paramsJson) {
      return undefined;
    }
    try {
      return JSON.parse(paramsJson)?.['FORK_CONTEXT_ID'];
    } catch {
      return undefined;
    }
  }

  // INTERIM ISPI-481001 — remove the transient restore marker from the URL (it is never forwarded to AIR,
  // which only reads the 'params' JSON). Delete together with the block above.
  private stripForkRestoredMarker(params: Params): void {
    const cleaned: Record<string, string> = {...(params as Record<string, string>)};
    delete cleaned['awp-fork-restored'];
    const queryString: string = new URLSearchParams(cleaned).toString();
    const currentPath: string = this.location.path().split('?')[0];
    this.location.replaceState(queryString ? `${currentPath}?${queryString}` : currentPath);
  }

  /**
   * Logs out the current user and redirects back with all query params preserved.
   */
  private logOutAndRedirect(params: Params): void {
    console.log('[Webapp] [ServiceCaseBar] Re-login required. Logging out current user.');
    const queryString: string = new URLSearchParams(params as Record<string, string>).toString();
    const currentPath: string = this.location.path().split('?')[0];
    let redirectPath: string = queryString ? `${currentPath}?${queryString}` : currentPath;
    // INTERIM ISPI-481001 — START: mark the post-login restore so it does not log out again after re-login.
    if (this.extractForkContextId(params)) {
      redirectPath += `${redirectPath.includes('?') ? '&' : '?'}awp-fork-restored=1`;
    }
    // INTERIM ISPI-481001 — END
    this.userService.logOutWithRedirect(redirectPath);
  }

  private setUpResizeObserver(): void {
    // The observer callback runs outside Angular's change detection, so the new tab width must be rendered explicitly.
    const resizeObserver: ResizeObserver = new ResizeObserver((): void => {
      this.updateTabWidth();
      this.changeDetectorRef.markForCheck();
    });

    resizeObserver.observe(this.tabContainer?.nativeElement);
  }

  customerLanguagePopupRequired(): boolean {
    return this.customerLoader.isCustomerLanguageCheckEnabled();
  }
}
